import { SelloutReportItem } from "./service"
import { ReportType } from "./ReportsTable"

export type ReportMapEntry = {
	label: string
	reportType: ReportType
	items: SelloutReportItem[]
}

const v2BasicRegexp = /([^_]+)_([A-Za-z]{3})_(\d{4})_BASIC(_P\d+)?(.zip)$/
const v2ExtendedRegexp = /([^_]+)_([A-Za-z]{3})_(\d{2})_TO_([A-Za-z]{3})_(\d{2})_(\d{4})_EX(_P\d+)?(.zip)$/
const PART_GROUP_INDEX = 7

export const mapSelloutReportItems = (data: SelloutReportItem[]) => {
	const currentMap: ReportMapEntry[] = []
	data
		.filter(
			(report) =>
				v2BasicRegexp.test(report.filename) ||
				v2ExtendedRegexp.test(report.filename)
		)
		.forEach((report) => {
			addEntryToMap(currentMap, getMonthAndYearKey(report.date), report)
			if (
				report.reportType === "extended" &&
				!isSameMonthAndYear(report.date, report.endDate)
			) {
				addEntryToMap(currentMap, getMonthAndYearKey(report.endDate), report)
			}
		})
	sortLabels(currentMap)
	return currentMap
}

const getPeriod = (item: SelloutReportItem) => {
	if (isPeriodNeeded(item)) {
		const startDate = getParsedDate(item.date)
		const endDate = getParsedDate(item.endDate)
		return `${startDate
			.getUTCDate()
			.toString()
			.padStart(2, "0")}-${endDate.getUTCDate().toString().padStart(2, "0")}`
	}
	return undefined
}

const isPeriodNeeded = (item: SelloutReportItem): boolean => {
	const matches = v2ExtendedRegexp.exec(item.filename)
	if (matches !== null) {
		return isExtendedSplitOrThePart1(matches)
	}
	return false
}

const isExtendedSplitOrThePart1 = (matches: RegExpMatchArray) => {
	return (
		matches[PART_GROUP_INDEX] === undefined ||
		(matches[PART_GROUP_INDEX] !== undefined &&
			matches[PART_GROUP_INDEX] === "_P1")
	)
}

const isSameMonthAndYear = (startDate: string, endDate: string): boolean => {
	const start = getParsedDate(startDate)
	const end = getParsedDate(endDate)
	return (
		start.getUTCMonth() === end.getUTCMonth() &&
		start.getUTCFullYear() === end.getUTCFullYear()
	)
}

const addEntryToMap = (
	currentMap: ReportMapEntry[],
	key: string,
	item: SelloutReportItem
) => {
	const entry = currentMap.find((entry) => entry.label === key)
	const itemWithPeriod = { ...item, period: getPeriod(item) }
	if (entry !== undefined) {
		entry.items.push(itemWithPeriod)
	} else {
		currentMap.push({
			label: key,
			reportType: itemWithPeriod.reportType,
			items: [itemWithPeriod],
		})
	}
}

const getMonthAndYearKey = (date: string): string => {
	const parsedDate = getParsedDate(date)
	return `${(parsedDate.getUTCMonth() + 1)
		.toString()
		.padStart(2, "0")}-${parsedDate.getUTCFullYear()}`
}

const getParsedDate = (date: string): Date => {
	const dateSplit = date.split("-")
	if (dateSplit.length === 2) {
		return new Date(`${dateSplit[1]}-${dateSplit[0]}`)
	}
	if (dateSplit.length === 3) {
		return new Date(`${dateSplit[2]}-${dateSplit[1]}-${dateSplit[0]}`)
	}
	throw new Error(`Unexpected date format: ${date}`)
}

const sortLabels = (currentMap: ReportMapEntry[]) => {
	currentMap
		.sort((a, b) => {
			return getParsedDate(b.label).getTime() - getParsedDate(a.label).getTime()
		})
		.forEach((entry) =>
			entry.items.sort((a, b) => {
				const result =
					getParsedDate(a.date).getTime() - getParsedDate(b.date).getTime()
				if (result === 0) {
					return compareFilenames(a.filename, b.filename)
				}
				return result
			})
		)
}

const compareFilenames = (aFilename: string, bFilename: string): number => {
	if (aFilename < bFilename) return -1
	if (aFilename > bFilename) return 1
	return 0
}
